import { ChevronLeftIcon, ChevronRightIcon } from "@/assets/icons";
import { strings } from "@/constants";

const PAGE_NEIGHBOR_COUNT = 1;

const buildPagesHandler = (totalPages: number, currentPage: number): (number | "ellipsis")[] => {
    if (totalPages <= 7) return Array.from(
        { length: totalPages },
        (_, index) => index + 1,
    );

    const pages: (number | "ellipsis")[] = [1];

    const start = Math.max(
        2,
        currentPage - PAGE_NEIGHBOR_COUNT,
    );

    const end = Math.min(
        totalPages - 1,
        currentPage + PAGE_NEIGHBOR_COUNT,
    );

    if (start > 2) pages.push("ellipsis");

    for (let page = start; page <= end; page += 1) pages.push(page);

    if (end < totalPages - 1) pages.push("ellipsis");

    pages.push(totalPages);

    return pages;
};

export const Pagination = ({
    currentPage,
    onChange,
    pageSize,
    totalItems,
}: {
    currentPage: number,
    onChange: (page: number) => void,
    pageSize: number,
    totalItems: number,
}) => {
    const totalPages = Math.max(
        1,
        Math.ceil(totalItems / pageSize),
    );

    if (totalPages <= 1) return null;

    const safePage = Math.min(
        Math.max(
            1,
            currentPage,
        ),
        totalPages,
    );

    const pages = buildPagesHandler(
        totalPages,
        safePage,
    );

    const arrowClasses = `
        inline-flex
        size-9
        cursor-pointer
        items-center
        justify-center
        rounded-full
        border
        border-gray-200
        bg-white
        text-text-secondary
        transition-colors
        hover:border-primary
        hover:text-primary
        disabled:cursor-not-allowed
        disabled:opacity-40
        disabled:hover:border-gray-200
        disabled:hover:text-text-secondary
    `;

    const numberClasses = (isActive: boolean): string => `
        inline-flex
        size-9
        cursor-pointer
        items-center
        justify-center
        rounded-full
        text-sm
        font-medium
        transition-colors
        ${isActive
        ? "bg-primary text-white shadow-sm"
        : "text-text-secondary hover:bg-gray-100 hover:text-text-primary"}
    `;

    return (
        <nav
            aria-label="Pagination"
            className="
                flex
                items-center
                justify-center
                gap-1.5
                pt-2
                sm:gap-2
            "
        >
            <button
                aria-label={strings.common.previousPage}
                className={arrowClasses}
                disabled={safePage === 1}
                type="button"
                onClick={() => onChange(safePage - 1)}
            >
                <ChevronLeftIcon className="size-4" />
            </button>
            {pages.map((entry, index) => entry === "ellipsis"
                ? (
                    <span
                        key={`ellipsis-${index}`}
                        className="
                            inline-flex
                            size-9
                            items-center
                            justify-center
                            text-sm
                            text-text-secondary
                        "
                    >
                        …
                    </span>
                )
                : (
                    <button
                        key={entry}
                        aria-current={entry === safePage ? "page" : undefined}
                        className={numberClasses(entry === safePage)}
                        type="button"
                        onClick={() => onChange(entry)}
                    >
                        {entry}
                    </button>
                ))}
            <button
                aria-label={strings.common.nextPage}
                className={arrowClasses}
                disabled={safePage === totalPages}
                type="button"
                onClick={() => onChange(safePage + 1)}
            >
                <ChevronRightIcon className="size-4" />
            </button>
        </nav>
    );
};
